import { Injectable } from "@angular/core";
import { NEVER, Observable } from "rxjs";

/** Callback response of a SIBO panel function */
export interface ReponseObject {
  /** Response status code as string */
  name: string;
  /** Response status code */
  code: number;
  /** String complete message of callback */
  _message: string;
  /** Extra data of response */
  data: object;
}

/** Only for Q8919 device, led configuration object */
export interface LedConfiguration {
  red: number;
  green: number;
  blue: number;
}

/** Relay closing configuration object */
export interface RelayCloseConfiguration {
  /** Relay number to close (1 or 2 in Q896S) */
  relayNumber: number;
  /** Millisecond time of off delay */
  onDelay: number;
  /** Millisecond time of staircase time duration */
  stairCaseDuration: number;
}

/** Relay opening configuration object */
export interface RelayOpenConfiguration {
  /** Relay number to open (1 or 2 in Q896S) */
  relayNumber: number;
  /** Millisecond time of off duration */
  offDelay: number;
}

export interface ShowStatusBarConfiguration {
  showStatusBar: boolean;
}

/**
 * No-op replacement of the SIBO native handler (cordova-plugin-sibohandler), which Digital Place no longer
 * ships: the app never runs on a MyTo panel, so BIGService.isMyToDevice stays false.
 *
 * Every method resolves with an empty-but-valid response so callers that just
 * `await` without inspecting the result continue to work, and JSON.parse on
 * `response.data` produces an empty object without throwing.
 */
@Injectable({ providedIn: "root" })
export class SiboHandlerStub {
  input1State = false;
  input2State = false;

  private ok(): Promise<ReponseObject> {
    // `data` is typed as `object` but real callers treat it as a JSON string
    // via `JSON.parse(response.data.toString())`. Returning `{}` satisfies the
    // type; the JSON.parse will throw "[object Object]" → caller catch handles
    // it gracefully (returns null → downstream code already null-safe).
    return Promise.resolve({
      code: 0,
      name: "OK",
      _message: "stub",
      data: {},
    });
  }

  CheckCompatibility(): Promise<ReponseObject> {
    return this.ok();
  }
  Connect(): Promise<ReponseObject> {
    return this.ok();
  }
  Disconnect(): Promise<ReponseObject> {
    return this.ok();
  }
  ChangeLEDColor(_: LedConfiguration): Promise<ReponseObject> {
    return this.ok();
  }
  OpenRelay(_: RelayOpenConfiguration): Promise<ReponseObject> {
    return this.ok();
  }
  CloseRelay(_: RelayCloseConfiguration): Promise<ReponseObject> {
    return this.ok();
  }
  EnableInput1(): Promise<ReponseObject> {
    return this.ok();
  }
  DisableInput1(): Promise<ReponseObject> {
    return this.ok();
  }
  ShowStatusBar(_: ShowStatusBarConfiguration): Promise<ReponseObject> {
    return this.ok();
  }
  RebootDevice(): Promise<ReponseObject> {
    return this.ok();
  }
  InitBACnetClient(): Promise<ReponseObject> {
    return this.ok();
  }
  DisposeBACnetClient(): Promise<ReponseObject> {
    return this.ok();
  }
  WriteBACnetObject(
    _BACnetDeviceID: number,
    _BACnetObjectType: number,
    _objectInstance: number,
    _BACnetPropertyIdentifier: number,
    _arrayIndex: number,
    _priority: number,
    _value: string,
  ): Promise<ReponseObject> {
    return this.ok();
  }
  OnInput1Change(): Observable<any> {
    return NEVER;
  }
  EnableInput2(): Promise<ReponseObject> {
    return this.ok();
  }
  DisableInput2(): Promise<ReponseObject> {
    return this.ok();
  }
  OnInput2Change(): Observable<any> {
    return NEVER;
  }
  ReadBlock(_blockIndex: Number, _callStamp: string): Promise<ReponseObject> {
    return this.ok();
  }
  StopReading(_callStamp: string): Promise<ReponseObject> {
    return this.ok();
  }
  LoadKey(_key: String, _callStamp: string): Promise<ReponseObject> {
    return this.ok();
  }
  ReadUID(_callStamp: string): Promise<ReponseObject> {
    return this.ok();
  }
  SetBrightness(_percentageBrightness: Number): Promise<ReponseObject> {
    return this.ok();
  }
  SetProximitySensor(
    _activate: boolean,
    _time: Number,
    _percentageBrightness: Number,
    _defaultPercentage: Number,
  ): Promise<ReponseObject> {
    return this.ok();
  }
}
